"use client";

import type { FC } from "react";
import { formatBalance, formatFiatCurrency } from "@cede/utils";
import { UsdtIcon } from "@/components/icons";
import { TokenIcon } from "@/components/icons/library/tokens/TokenIcon";
import { useFormatCurrency } from "@/hooks/currency";
import { cn } from "@/lib/utils";
import { getPrizeNumber } from "@/utils/campaign";

interface CurrencyRewardDisplayProps {
  /**
   * The reward value to display (as number or string like "$1,000").
   * Denominated in `tokenSymbol` when provided, otherwise USDT.
   */
  usdtValue: number | string | null | undefined;
  /**
   * Reward token symbol (e.g. "WETH"). When set to a non-USDT token the
   * token's icon is shown instead of the USDT one and the local-currency
   * conversion is skipped — the value is in token units, not dollars.
   * Defaults to USDT behavior when omitted/null.
   */
  tokenSymbol?: string | null;
  /**
   * Whether to show the USDT icon
   * @default true
   */
  showIcon?: boolean;
  /**
   * Whether to show local currency (skipped for USD)
   * @default true
   */
  showLocalCurrency?: boolean;
  /**
   * Layout mode for displaying values
   * - "inline": USDT and local on same line with parentheses
   * - "stacked": Local currency on new line below USDT, no parentheses
   * @default "inline"
   */
  layout?: "inline" | "stacked";
  /**
   * Use compact notation for large numbers
   * @default false
   */
  compact?: boolean;
  /**
   * Additional class names for the container
   */
  className?: string;
  /**
   * Additional class names for the USDT value
   */
  usdtClassName?: string;
  /**
   * Additional class names for the local currency value
   */
  localClassName?: string;
  /**
   * Icon size
   * @default 16
   */
  iconSize?: number;
}

/**
 * Parse reward values and reuse campaign parsing logic for string inputs.
 */
function parseRewardValue(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  return /\d/.test(value) ? getPrizeNumber(value) : null;
}

/**
 * Format a number as a reward string (without currency symbol)
 */
function formatRewardNumber(value: number): string {
  if (value >= 10) {
    return formatBalance(Math.round(value));
  }
  return formatBalance(value, { roundForFiat: true, preventRoundingUp: true });
}

/**
 * Display component for showing USDT rewards with optional local currency conversion
 *
 * For non-USD currencies:
 * - inline layout: [USDT icon] 1,000 (Rp 15,800,000)
 * - stacked layout: [USDT icon] 1,000
 *                   Rp 15,800,000
 * For USD, shows: [USDT icon] 1,000
 *
 * @example
 * <CurrencyRewardDisplay usdtValue={1000} />
 * <CurrencyRewardDisplay usdtValue={1000} layout="stacked" />
 */
export const CurrencyRewardDisplay: FC<CurrencyRewardDisplayProps> = ({
  usdtValue,
  tokenSymbol,
  showIcon = true,
  showLocalCurrency = true,
  layout = "inline",
  compact = false,
  className,
  usdtClassName,
  localClassName,
  iconSize = 16,
}) => {
  const { selectedCurrency, rates, isUsd } = useFormatCurrency();

  const numericValue = parseRewardValue(usdtValue);

  if (numericValue == null) {
    return <span className="text-sm text-cede-text-tertiary">-</span>;
  }

  const isUsdtDenominated = tokenSymbol == null || tokenSymbol.toUpperCase() === "USDT";

  // Format the reward display value
  const usdtDisplay = formatRewardNumber(numericValue);

  // Calculate and format local currency value. Only meaningful for USDT
  // (≈USD) rewards — a token-denominated value can't go through USD rates.
  const currencyKey = selectedCurrency.toLowerCase();
  const rate = rates?.[currencyKey];
  const shouldShowLocal = showLocalCurrency && isUsdtDenominated && !isUsd && rate != null;
  let localDisplay: string | null = null;

  if (shouldShowLocal && rate != null) {
    const localValue = numericValue * rate;
    localDisplay = formatFiatCurrency(localValue, { currencyCode: selectedCurrency, compact });
  }

  const isStacked = layout === "stacked";

  return (
    <span
      className={cn(
        isStacked ? "inline-flex flex-col items-center" : "inline-flex items-center gap-1 flex-wrap",
        className,
      )}
    >
      {/* Reward value with its token icon */}
      <span className={cn("inline-flex items-center gap-1 leading-none", usdtClassName)}>
        {showIcon &&
          (isUsdtDenominated ? (
            <UsdtIcon width={iconSize} height={iconSize} className="shrink-0" />
          ) : (
            <TokenIcon symbol={tokenSymbol} width={iconSize} height={iconSize} className="shrink-0" />
          ))}
        <span>{usdtDisplay}</span>
      </span>

      {/* Local currency - stacked (no parens) or inline (with parens) */}
      {shouldShowLocal && localDisplay && (
        <span className={cn("text-cede-text-secondary text-[0.85em] leading-none", localClassName)}>
          {isStacked ? localDisplay : `(${localDisplay})`}
        </span>
      )}
    </span>
  );
};
